/**
 * Centralized Authorization Configuration for HRM System
 *
 * This file contains all authorization rules for different modules and features.
 * It provides a single source of truth for access control across the application.
 */

/**
 * @typedef {Object} AuthConfig
 * @property {string[]} [roles] - Required roles for access
 * @property {string} [module] - Required module for access
 * @property {string} [feature] - Required feature for access
 * @property {boolean} [requireAll=false] - If true, user must have ALL specified roles/features
 * @property {string} [fallback="access-denied"] - Type of fallback ("access-denied" or "upgrade-plan")
 * @property {string} [description] - Description of what this authorization controls
 */

/**
 * Centralized Authorization Configuration for CRM–ERP–INVENTORY System
 */

/** ================================
 * ROLE DEFINITIONS
 * ================================ */
export const ROLES = {
  ADMIN: "ROLE_ADMIN",

  // CRM
  CRM_ADMIN: "ROLE_CRM-ADMIN",
  SALES_MANAGER: "ROLE_SALES-MANAGER",
  SALES_EMPLOYEE: "ROLE_SALES-EMPLOYEE",

  // ERP
  ERP_ADMIN: "ROLE_ERP-ADMIN",
  PRODUCTION_MANAGER: "ROLE_PRODUCTION-MANAGER",
  OPERATIONS_EMPLOYEE: "ROLE_OPERATIONS-EMPLOYEE",

  // Inventory
  INVENTORY_MANAGER: "ROLE_INVENTORY-MANAGER",
};

/** ================================
 * ADMIN (Company Details Only)
 * ================================ */
export const ADMIN_AUTH = {
  COMPANY_DETAILS: {
    roles: [
      ROLES.ADMIN,
      ROLES.CRM_ADMIN,
      ROLES.ERP_ADMIN,
      ROLES.INVENTORY_MANAGER,
    ],
    feature: "ADMIN_COMPANY_DETAILS",
    fallback: "access-denied",
    description: "Access and manage company profile and business settings.",
  },
};

/** ================================
 * CRM MODULE
 * ================================ */
export const CRM_AUTH = {
  // CRM_ENQUIRY_STAGES
  MANAGE_ENQUIRY_STAGES: {
    roles: [ROLES.CRM_ADMIN],
    feature: "CRM_MANAGE_ENQUIRY_STAGES",
    description: "Create and manage enquiry stages.",
  },
};

/** ================================
 * ERP MODULE
 * ================================ */
export const ERP_AUTH = {
  // ERP_WAREHOUSE
  MANAGE_WAREHOUSE: {
    roles: [ROLES.ERP_ADMIN],
    feature: "ERP_MANAGE_WAREHOUSE",
    description: "Manage ERP warehouses.",
  },

  // ERP_OPERATION_STAGES
  MANAGE_OPERATION_STAGES: {
    roles: [ROLES.ERP_ADMIN],
    feature: "ERP_MANAGE_OPERATION_STAGES",
    description: "Manage all ERP operation stages.",
  },
};

/** ================================
 * INVENTORY MODULE
 * ================================ */
export const INVENTORY_AUTH = {
  // INV_BRANDS → all 3 roles
  MANAGE_BRANDS: {
    roles: [ROLES.CRM_ADMIN, ROLES.ERP_ADMIN, ROLES.INVENTORY_MANAGER],
    feature: "INV_MANAGE_BRANDS",
    description: "Manage inventory brands.",
  },

  // INV_RAW_MATERIALS → ERP Admin + Inventory Manager
  MANAGE_RAW_MATERIALS: {
    roles: [ROLES.ERP_ADMIN, ROLES.INVENTORY_MANAGER],
    feature: "INV_MANAGE_RAW_MATERIALS",
    description: "Manage raw materials.",
  },

  // INV_PRODUCT_CATEGORY → all 3 roles
  MANAGE_PRODUCT_CATEGORY: {
    roles: [ROLES.CRM_ADMIN, ROLES.ERP_ADMIN, ROLES.INVENTORY_MANAGER],
    feature: "INV_MANAGE_PRODUCT_CATEGORY",
    description: "Manage product categories.",
  },

  // INV_PRODUCTS → all 3 roles
  MANAGE_PRODUCTS: {
    roles: [ROLES.CRM_ADMIN, ROLES.ERP_ADMIN, ROLES.INVENTORY_MANAGER],
    feature: "INV_MANAGE_PRODUCTS",
    description: "Manage all inventory products.",
  },

  // INV_STOCK_LEVELS (stock update) → all 3 roles
  MANAGE_STOCK_LEVELS: {
    roles: [ROLES.CRM_ADMIN, ROLES.ERP_ADMIN, ROLES.INVENTORY_MANAGER],
    feature: "INV_MANAGE_STOCK_LEVELS",
    description: "Manage and update stock levels.",
  },

  // INV_SUPPLIERS → all 3 roles
  MANAGE_SUPPLIERS: {
    roles: [ROLES.CRM_ADMIN, ROLES.ERP_ADMIN, ROLES.INVENTORY_MANAGER],
    feature: "INV_MANAGE_SUPPLIERS",
    description: "Manage suppliers.",
  },
};

/** ================================
 * MASTER COMBINED CONFIG
 * ================================ */
export const ALL_AUTH_CONFIGS = {
  ADMIN: ADMIN_AUTH,
  CRM: CRM_AUTH,
  ERP: ERP_AUTH,
  INVENTORY: INVENTORY_AUTH,
};

/** ================================
 * HELPERS
 * ================================ */
export const getAuthConfig = (module, feature) => {
  const moduleConfig = ALL_AUTH_CONFIGS[module.toUpperCase()];
  if (!moduleConfig) return null;
  return moduleConfig[feature.toUpperCase()] || null;
};

export const getFeaturesForRole = (role) => {
  const features = [];
  Object.values(ALL_AUTH_CONFIGS).forEach((moduleConfig) => {
    Object.values(moduleConfig).forEach((cfg) => {
      if (cfg.roles?.includes(role)) features.push(cfg.feature);
    });
  });
  return features;
};

export const requiresUpgrade = (module, feature) => {
  const config = getAuthConfig(module, feature);
  return config?.fallback === "upgrade-plan";
};
